import React from 'react';
import { Link } from 'react-router-dom';
import { Competition } from '../../types/interfaces';
import CompetitionTypePill from '../../components/common/CompetitionTypePill';
import CompetitionVisibilityPill from '../../components/common/CompetitionVisibilityPill';
import Skeleton, { SkeletonText } from '../../components/common/Skeleton';
import { competitionTiming } from './competitionTiming';
import './CompetitionCard.css';

const PeopleIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

/** One competition in the All competitions grid. The whole card opens the competition. */
const CompetitionCard: React.FC<{ competition: Competition; now?: Date }> = ({ competition: c, now }) => {
  const status = (c.status || '').toLowerCase();
  const timing = competitionTiming(c, now);
  const isTeam = (c.competition_mode || 'individual') === 'team';
  const unit = c.participants === 1 ? (isTeam ? 'team' : 'participant') : (isTeam ? 'teams' : 'participants');
  const hasMoneyPrize = (c.prize || '').toLowerCase() === 'money';
  const initial = (c.title || '?').trim().charAt(0).toUpperCase();

  return (
    <article className={`competition-card competition-card--${status || 'unknown'}`}>
      <div className="competition-card__top">
        {c.logo_path ? (
          <img src={c.logo_path} alt="" className="competition-card__logo" />
        ) : (
          <span className="competition-card__logo competition-card__logo--initial" aria-hidden="true">{initial}</span>
        )}
        <span className={`dashboard__competition-status dashboard__competition-status--${status}`}>{c.status}</span>
      </div>

      <h3 className="competition-card__title">
        <Link to={`/competition/${c.id}`} className="competition-card__link">{c.title}</Link>
      </h3>
      {c.description && <p className="competition-card__description">{c.description}</p>}

      <div className="competition-card__pills">
        <CompetitionTypePill type={c.type} />
        <CompetitionVisibilityPill visibility={c.visibility} />
      </div>

      {timing && (
        <div className={`competition-card__timing${timing.urgent ? ' competition-card__timing--urgent' : ''}`}>
          <span className="competition-card__timing-label">{timing.label}</span>
          {timing.progress !== null && (
            <span className="competition-card__track" aria-hidden="true">
              <span className="competition-card__fill" style={{ width: `${Math.round(timing.progress * 100)}%` }} />
            </span>
          )}
        </div>
      )}

      <div className="competition-card__footer">
        {c.participants !== undefined ? (
          <span className="competition-card__people"><PeopleIcon />{c.participants} {unit}</span>
        ) : <span />}
        {hasMoneyPrize && <span className="competition-card__prize" title="Money prize" aria-label="Money prize">$$</span>}
        <span className="competition-card__open" aria-hidden="true">View <span className="competition-card__arrow">&rarr;</span></span>
      </div>
    </article>
  );
};

/** Same shape as a card, shown while the list loads. */
export const CompetitionCardSkeleton: React.FC = () => (
  <div className="competition-card competition-card--skeleton" aria-hidden="true">
    <div className="competition-card__top">
      <Skeleton width={48} height={48} radius={12} />
      <Skeleton width={64} height={22} radius={999} />
    </div>
    <Skeleton width="70%" height="1.25rem" />
    <SkeletonText lines={3} />
    <div className="competition-card__pills">
      <Skeleton width={56} height={22} radius={999} />
      <Skeleton width={68} height={22} radius={999} />
    </div>
    <Skeleton height={6} radius={999} />
    <div className="competition-card__footer">
      <Skeleton width={110} height="0.9rem" />
      <Skeleton width={48} height="0.9rem" />
    </div>
  </div>
);

export default CompetitionCard;
